import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { createPortal } from 'react-dom'
import { FILTER_SHOW_ALL } from '@constants/Notifications'
import Accordion from '../Accordion/Accordion.component'
import AccordionItem from '../Accordion/AccordionItem.component'
import Checkbox from '../Checkbox/Checkbox.component'
import Search from './Search.component'
import type {
  FilterSelection,
  SearchWithFilterProps,
} from './SearchWithFilter.types'
import './SearchWithFilter.css'

type MenuPosition = {
  top: number
  left: number
}

const MENU_GAP = 8

const pruneSelection = (
  selection: FilterSelection,
  filters: Record<string, string[]>,
): FilterSelection => {
  const next: FilterSelection = {}
  Object.keys(filters).forEach((key) => {
    const allowed = new Set(filters[key] ?? [])
    const selected = (selection[key] ?? []).filter((value) =>
      allowed.has(value),
    )
    if (selected.length > 0) {
      next[key] = selected
    }
  })
  return next
}

const SearchWithFilter: React.FC<SearchWithFilterProps> = ({
  filters,
  filterLabels = {},
  selectedFilters: selectedFiltersProp,
  defaultSelectedFilters,
  onFilterChange,
  showResetButton = false,
  onReset,
  customRenderers = {},
  showFilter = true,
  filterOpen,
  defaultFilterOpen = false,
  onFilterOpenChange,
  value,
  defaultValue,
  placeholder,
  disabled = false,
  darkMode = false,
  className = '',
  ariaLabel,
  onChange,
  onClear,
}) => {
  const rootRef = useRef<HTMLDivElement>(null)
  const filterButtonRef = useRef<HTMLButtonElement>(null)
  const menuRef = useRef<HTMLDivElement>(null)

  const [internalSelected, setInternalSelected] = useState<FilterSelection>(
    () => defaultSelectedFilters ?? {},
  )
  const [internalOpen, setInternalOpen] = useState(defaultFilterOpen)
  const [position, setPosition] = useState<MenuPosition | null>(null)

  const isSelectionControlled = selectedFiltersProp !== undefined
  const selectedFilters = useMemo(
    () =>
      pruneSelection(
        isSelectionControlled ? (selectedFiltersProp ?? {}) : internalSelected,
        filters,
      ),
    [filters, internalSelected, isSelectionControlled, selectedFiltersProp],
  )

  const isOpenControlled = filterOpen !== undefined
  const isOpen = showFilter && (isOpenControlled ? filterOpen : internalOpen)

  const setOpen = useCallback(
    (nextOpen: boolean): void => {
      if (!isOpenControlled) {
        setInternalOpen(nextOpen)
      }
      onFilterOpenChange?.(nextOpen)
    },
    [isOpenControlled, onFilterOpenChange],
  )

  const emitSelection = useCallback(
    (next: FilterSelection): void => {
      const pruned = pruneSelection(next, filters)
      if (!isSelectionControlled) {
        setInternalSelected(pruned)
      }
      onFilterChange?.(pruned)
    },
    [filters, isSelectionControlled, onFilterChange],
  )

  useEffect(() => {
    if (isSelectionControlled) {
      return
    }
    setInternalSelected((prev) => {
      const next = pruneSelection(prev, filters)
      const prevKeys = Object.keys(prev)
      const nextKeys = Object.keys(next)
      if (
        prevKeys.length === nextKeys.length &&
        nextKeys.every(
          (key) =>
            prev[key]?.length === next[key]?.length &&
            (prev[key] ?? []).every(
              (item, index) => item === next[key]?.[index],
            ),
        )
      ) {
        return prev
      }
      return next
    })
  }, [filters, isSelectionControlled])

  const updatePosition = useCallback((): void => {
    const trigger = filterButtonRef.current
    const menu = menuRef.current
    const field =
      (rootRef.current?.querySelector(
        '.ds-search__field',
      ) as HTMLElement | null) ?? null
    if (!trigger || !menu) {
      return
    }

    const triggerRect = trigger.getBoundingClientRect()
    const fieldRect = field?.getBoundingClientRect() ?? triggerRect
    const menuRect = menu.getBoundingClientRect()

    let left = triggerRect.right - menuRect.width
    if (left < MENU_GAP) {
      left = fieldRect.left
    }
    if (left + menuRect.width > window.innerWidth - MENU_GAP) {
      left = Math.max(MENU_GAP, window.innerWidth - menuRect.width - MENU_GAP)
    }

    // 8px below the search field (not the inner filter button)
    let top = fieldRect.bottom + MENU_GAP
    if (top + menuRect.height > window.innerHeight - MENU_GAP) {
      top = fieldRect.top - MENU_GAP - menuRect.height
    }

    setPosition((prev) => {
      if (prev && prev.top === top && prev.left === left) {
        return prev
      }
      return { top, left }
    })
  }, [])

  useLayoutEffect(() => {
    if (!isOpen) {
      setPosition(null)
      return
    }
    updatePosition()
  }, [isOpen, updatePosition])

  useEffect(() => {
    if (!isOpen) {
      return undefined
    }

    const handleReposition = (): void => updatePosition()
    window.addEventListener('scroll', handleReposition, true)
    window.addEventListener('resize', handleReposition)

    return () => {
      window.removeEventListener('scroll', handleReposition, true)
      window.removeEventListener('resize', handleReposition)
    }
  }, [isOpen, updatePosition])

  useEffect(() => {
    if (!isOpen) {
      return undefined
    }

    const handlePointerDown = (event: MouseEvent): void => {
      const target = event.target as Node
      if (
        !rootRef.current?.contains(target) &&
        !menuRef.current?.contains(target)
      ) {
        setOpen(false)
      }
    }

    document.addEventListener('mousedown', handlePointerDown)
    return () => document.removeEventListener('mousedown', handlePointerDown)
  }, [isOpen, setOpen])

  useEffect(() => {
    if (!isOpen) {
      return undefined
    }

    const handleKeyDown = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') {
        setOpen(false)
      }
    }

    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [isOpen, setOpen])

  const handleCheckboxChange = useCallback(
    (category: string, option: string): void => {
      const currentSelections = selectedFilters[category] || []
      const hasShowAll = filters[category]?.includes(FILTER_SHOW_ALL) ?? false

      let nextSelections: string[]

      if (hasShowAll && option === FILTER_SHOW_ALL) {
        nextSelections = currentSelections.includes(FILTER_SHOW_ALL)
          ? currentSelections.filter((item) => item !== FILTER_SHOW_ALL)
          : [FILTER_SHOW_ALL]
      } else if (currentSelections.includes(option)) {
        const removed = currentSelections.filter((item) => item !== option)
        nextSelections =
          hasShowAll && removed.length === 0
            ? [FILTER_SHOW_ALL]
            : removed.filter((item) => item !== FILTER_SHOW_ALL)
      } else {
        nextSelections = [
          ...currentSelections.filter((item) => item !== FILTER_SHOW_ALL),
          option,
        ]
      }

      emitSelection({
        ...selectedFilters,
        [category]: nextSelections,
      })
    },
    [emitSelection, filters, selectedFilters],
  )

  const handleReset = useCallback((): void => {
    emitSelection({})
    onReset?.()
  }, [emitSelection, onReset])

  const categoryKeys = useMemo(() => Object.keys(filters), [filters])

  const rootClassNames = [
    'ds-search-with-filter',
    darkMode ? 'ds-search-with-filter--dark' : '',
    className,
  ]
    .filter(Boolean)
    .join(' ')

  const menuClassNames = [
    'ds-search-with-filter__menu',
    darkMode ? 'ds-search-with-filter__menu--dark' : '',
  ]
    .filter(Boolean)
    .join(' ')

  const menu =
    isOpen && typeof document !== 'undefined'
      ? createPortal(
          <div
            ref={menuRef}
            className={menuClassNames}
            role="dialog"
            aria-label="Search filters"
            style={{
              top: position?.top ?? 0,
              left: position?.left ?? 0,
              visibility: position ? 'visible' : 'hidden',
            }}
            onMouseDown={(event) => event.stopPropagation()}
          >
            {showResetButton ? (
              <div className="ds-search-with-filter__reset">
                <button
                  type="button"
                  className="ds-search-with-filter__reset-btn"
                  onClick={handleReset}
                >
                  <i className="mgc mgc_refresh_2_line" aria-hidden="true" />
                  Reset filter
                </button>
              </div>
            ) : null}

            <Accordion
              allowMultiple
              darkMode={darkMode}
              className="ds-search-with-filter__accordion"
              defaultOpenIds={categoryKeys.slice(0, 1)}
            >
              {categoryKeys.map((key) => (
                <AccordionItem
                  key={key}
                  id={key}
                  title={filterLabels[key] || key}
                  darkMode={darkMode}
                >
                  <div className="ds-search-with-filter__options">
                    {(filters[key] || []).map((option) => {
                      const rendererKey = `${key}:${option}`
                      const customRenderer = customRenderers[rendererKey]
                      const isChecked = (selectedFilters[key] || []).includes(
                        option,
                      )
                      const toggle = (): void =>
                        handleCheckboxChange(key, option)

                      if (customRenderer) {
                        return (
                          <div
                            key={option}
                            className="ds-search-with-filter__option"
                          >
                            {customRenderer(option, isChecked, toggle)}
                          </div>
                        )
                      }

                      return (
                        <Checkbox
                          key={option}
                          label={option}
                          checked={isChecked}
                          darkMode={darkMode}
                          onChange={toggle}
                          className="ds-search-with-filter__option"
                        />
                      )
                    })}
                  </div>
                </AccordionItem>
              ))}
            </Accordion>
          </div>,
          document.body,
        )
      : null

  return (
    <div ref={rootRef} className={rootClassNames}>
      <Search
        value={value}
        defaultValue={defaultValue}
        placeholder={placeholder}
        disabled={disabled}
        darkMode={darkMode}
        ariaLabel={ariaLabel}
        showFilter={showFilter}
        allowClearWithFilter={showFilter}
        filterExpanded={isOpen}
        filterButtonRef={filterButtonRef}
        onChange={onChange}
        onClear={onClear}
        onFilterClick={() => {
          if (disabled || !showFilter) {
            return
          }
          setOpen(!isOpen)
        }}
      />
      {menu}
    </div>
  )
}

export default SearchWithFilter
